import { API_BASE_URL } from "../config/api";

export class ApiError extends Error {
  constructor(message, { status, body, errors } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.errors = errors ?? null;
  }
}

/**
 * Prefer Laravel field `errors` (e.g. email already taken) over generic `reason`.
 */
export function formatApiErrorMessage(envelope, fallback = "Request failed.") {
  const fieldErrors = envelope?.errors;
  if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
    const messages = Object.values(fieldErrors)
      .flat()
      .map((msg) => String(msg ?? "").trim())
      .filter(Boolean);
    if (messages.length) {
      return [...new Set(messages)].join(" ");
    }
  }

  const reason = String(envelope?.reason ?? "").trim();
  if (
    reason &&
    !/^something went wrong\.?$/i.test(reason) &&
    !/^oops something went wrong/i.test(reason)
  ) {
    return reason;
  }

  return envelope?.message || fallback;
}

function throwFromEnvelope(envelope, json, status) {
  throw new ApiError(formatApiErrorMessage(envelope, status ? `Request failed (${status}).` : "Request failed."), {
    status,
    body: json,
    errors: envelope?.errors ?? null,
  });
}

/**
 * Laravel envelope: { data: { status_code, message, in_error, reason, data|errors, point_in_time } }
 * Treats `in_error: false` as success (including status_code "001" for created / email-sent).
 */
export function unwrapApiEnvelope(json) {
  const envelope = json?.data ?? json;

  if (envelope?.in_error) {
    throwFromEnvelope(envelope, json);
  }

  const code = envelope?.status_code;
  if (code && code !== "000" && code !== "001" && envelope?.in_error !== false) {
    throwFromEnvelope(envelope, json);
  }

  return envelope?.data ?? envelope;
}

export async function apiRequest(path, { method = "GET", body, token, headers = {} } = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        ...(body != null ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body != null ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    const hint =
      typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
        ? ` Start the API: cd backend && php artisan serve (expects ${API_BASE_URL}).`
        : ` Check REACT_APP_API_BASE_URL (currently ${API_BASE_URL}).`;
    throw new ApiError(
      `Cannot reach the API (${url}).${hint}`,
      { status: 0, body: null, errors: null, cause: error }
    );
  }

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const envelope = json?.data ?? json;
    throwFromEnvelope(envelope, json, response.status);
  }

  return json;
}
